import React, { useEffect, useState, createContext, useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import "./buzz.css";

export const BuzzMeContext = createContext(null);
export const useBuzzMe = () => useContext(BuzzMeContext);

/** «Συνέχισε την επιβεβαίωση»: όποιος έφυγε από το signup πριν βάλει τον
    6ψήφιο κωδικό, τον βάζει εδώ — ίδια endpoints με το wizard. */
function VerifyEmailBanner({ email, onVerified }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resent, setResent] = useState(false);

  const verify = async () => {
    setBusy(true); setError(null);
    try {
      await axios.post(`${config.API_BASE_URL}/buzz/verify-email`, { code });
      onVerified();
    } catch {
      setError(t("Wrong or expired code."));
    } finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true); setError(null); setResent(false);
    try {
      await axios.post(`${config.API_BASE_URL}/buzz/verify-email/resend`);
      setResent(true);
    } catch {
      setError(t("Something went wrong — please try again."));
    } finally { setBusy(false); }
  };

  return (
    <div className="bz-banner" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <span>
        📬 <b>{t("Verify your email to secure your account.")}</b>{" "}
        {t("Enter the 6-digit code we sent to")} <b>{email}</b>:
      </span>
      <input type="text" inputMode="numeric" maxLength={6} value={code}
        placeholder="______"
        style={{ width: 110, border: "1px solid #d1d5db", borderRadius: 8, padding: "7px 10px",
                 letterSpacing: 4, textAlign: "center", fontSize: 15 }}
        onChange={e => setCode(e.target.value.replace(/\D/g, ""))} />
      <button className="bz-btn primary" disabled={busy || code.length !== 6} onClick={verify}>
        {t("Verify")}
      </button>
      <button className="bz-btn ghost" disabled={busy} onClick={resend}>
        {t("Resend code")}
      </button>
      {resent && <span className="bz-hint">✓ {t("New code sent — it usually arrives within 1-2 minutes.")}</span>}
      {error && <span style={{ color: "#991b1b", fontWeight: 600 }}>{error}</span>}
    </div>
  );
}

/** Κοινό περίβλημα των /buzz/* σελίδων: nav + trial banner + email verification banner. */
export default function BuzzShell({ children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  const [demoModal, setDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: "", phone: "", message: "" });
  const [demoSent, setDemoSent] = useState(false);

  const sendDemoLead = async () => {
    await axios.post(`${config.API_BASE_URL}/buzz/lead`, demoForm);
    setDemoSent(true);
  };

  const loadMe = async () => {
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/buzz/me`);
      setMe(data);
      setErr(null);
    } catch (e) {
      if (e?.response?.status === 403) setErr("forbidden");
    }
  };
  useEffect(() => { loadMe(); }, []);

  // Όσο η υπηρεσία είναι ανενεργή, κρύβουμε το κεντρικό μενού του navbar
  // (μένουν brand/αποσύνδεση/γλώσσα) — CSS hook στο buzz.css.
  useEffect(() => {
    if (me?.product?.status === "inactive") document.body.dataset.buzzInactive = "1";
    else delete document.body.dataset.buzzInactive;
    return () => { delete document.body.dataset.buzzInactive; };
  }, [me?.product?.status]);

  const trialDaysLeft = me?.product?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(me.product.trialEndsAt) - Date.now()) / 86400000))
    : null;

  return (
    <BuzzMeContext.Provider value={{ me, reload: loadMe }}>
      <div className="bz-wrap">
        {/* Οι καρτέλες των σελίδων ζουν πλέον στη γραμμή του μενού (Navbar).
            Εξαίρεση: με ανενεργή συνδρομή το navbar κρύβεται σκόπιμα, οπότε
            μένει εδώ ο ένας δρόμος που έχει νόημα — η Συνδρομή. */}
        {me?.product?.status === "inactive" && (
          <div className="bz-nav">
            <NavLink to="/buzz/billing">{t("Subscription")}</NavLink>
          </div>
        )}

        {err === "forbidden" && (
          <div className="bz-banner err">{t("Buzz is not active for this account.")}</div>
        )}
        {me?.product?.status === "inactive" && !location.pathname.startsWith("/buzz/billing") && (
          <div className="bz-banner">
            🚀 <NavLink to="/buzz/billing">{t("Start your 15-day free trial")}</NavLink>
          </div>
        )}
        {/* Σε περιβάλλον πελάτη (impersonation) ο διαχειριστής μόνο ΒΛΕΠΕΙ:
            η επαλήθευση ανήκει στον πελάτη και γίνεται με το δικό του email. */}
        {me && !me.emailVerified && (me.impersonating ? (
          <div className="bz-banner">
            📬 {t("This customer has not verified their email yet.")}{" "}
            {me.email && <b>{me.email}</b>}
          </div>
        ) : (
          <VerifyEmailBanner email={me.email} onVerified={loadMe} />
        ))}
        {me?.product?.status === "trial" && trialDaysLeft !== null && (
          <div className="bz-banner">
            {t("Trial")}: {trialDaysLeft} {t("days left")}
          </div>
        )}
        {me?.product?.status === "grace" && (
          <div className="bz-banner err">{t("Payment issue — service will pause soon.")}</div>
        )}

        {children}

        {/* Funnel προς τα πλήρη πακέτα EstiaSync */}
        <div style={{ textAlign: "center", marginTop: 36, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
          <span className="bz-hint">
            ✨ {t("Want reservations, kitchen screens & ordering too?")}{" "}
            <a href="#demo" style={{ color: "#d97706", fontWeight: 600 }}
              onClick={(e) => { e.preventDefault(); setDemoSent(false); setDemoModal(true); }}>
              {t("Request a demo")}
            </a>
          </span>
        </div>

        {demoModal && (
          <div className="bz-modal-bg" onClick={() => setDemoModal(false)}>
            <div className="bz-modal" onClick={(e) => e.stopPropagation()} style={{ textAlign: "left" }}>
              {demoSent ? (
                <>
                  <h3>🤝 {t("Thank you!")}</h3>
                  <p className="bz-hint">{t("We'll contact you soon about the full EstiaSync platform.")}</p>
                  <button className="bz-btn" onClick={() => setDemoModal(false)}>{t("Close")}</button>
                </>
              ) : (
                <>
                  <h3>✨ {t("Request a demo")}</h3>
                  <p className="bz-hint">{t("Reservations, floor plan, kitchen & BBQ screens, ordering with QR — the full platform.")}</p>
                  <div className="bz-form-row">
                    <label>{t("Name")}</label>
                    <input type="text" value={demoForm.name} autoFocus
                      onChange={e => setDemoForm({ ...demoForm, name: e.target.value })} />
                  </div>
                  <div className="bz-form-row">
                    <label>{t("Phone")}</label>
                    <input type="text" value={demoForm.phone}
                      onChange={e => setDemoForm({ ...demoForm, phone: e.target.value })} />
                  </div>
                  <div className="bz-form-row">
                    <label>{t("Message (optional)")}</label>
                    <input type="text" value={demoForm.message}
                      onChange={e => setDemoForm({ ...demoForm, message: e.target.value })} />
                  </div>
                  <div className="bz-toolbar" style={{ marginTop: 10 }}>
                    <button className="bz-btn primary" onClick={sendDemoLead}
                      disabled={demoForm.name.length < 2 || demoForm.phone.length < 6}>
                      {t("Send")}
                    </button>
                    <button className="bz-btn" onClick={() => setDemoModal(false)}>{t("Close")}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </BuzzMeContext.Provider>
  );
}
